import { useMemo } from 'react';
import { useLoading, useSnackbar } from '@carcareers/contexts';
import { postApi } from '@carcareers/store';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { PostRequest } from '@libs/models';
import { ApiError, ApiResponse, PostRequestStatus } from '@libs/types';

interface PostRequestCallbacks {
  onSuccess?: () => void;
}

export const usePostRequest = () => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const [updateStatus] = postApi.usePostRequestUpdateStatusMutation();

  return useMemo(
    () => ({
      updatePostRequestStatus: async (
        id?: number,
        status?: PostRequestStatus,
        callbacks?: PostRequestCallbacks
      ): Promise<ApiResponse<PostRequest>> => {
        if (!id || !status) {
          toggleSnackbar('warning', 'Post request ID and status are required.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating post request status...');
        setLoading(true);
        const response = await updateStatus({ id, status });
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Post request status updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          if (errors.length !== 0) {
            toggleSnackbar('error', getErrorsAsString(response));
          }
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
    }),
    [updateStatus, setLoading, setLabel, toggleSnackbar]
  );
}; 